import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { BottomNavigation } from "@/components/layout/BottomNavigation";
import { useAuth } from "@/App";
import { 
  ChevronLeft,
  ArrowLeft,
  Clock,
  Play,
  Pause,
  RotateCcw,
  Coffee,
  Target,
  Check
} from "lucide-react";

const TIMER_MODES = {
  focus: { duration: 25 * 60, label: "Focus", color: "from-rose-500/30 to-red-500/30", textColor: "text-rose-400" },
  shortBreak: { duration: 5 * 60, label: "Short Break", color: "from-green-500/30 to-emerald-500/30", textColor: "text-green-400" },
  longBreak: { duration: 15 * 60, label: "Long Break", color: "from-blue-500/30 to-cyan-500/30", textColor: "text-blue-400" },
};

export default function FocusTimerPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mode, setMode] = useState("focus"); // focus, shortBreak, longBreak
  const [timeLeft, setTimeLeft] = useState(TIMER_MODES.focus.duration);
  const [isRunning, setIsRunning] = useState(false);
  const [sessions, setSessions] = useState(0);
  const [totalFocusTime, setTotalFocusTime] = useState(0);
  const intervalRef = useRef(null);
  const audioContextRef = useRef(null);

  const currentMode = TIMER_MODES[mode];

  const playSound = useCallback((frequency = 800, duration = 0.3) => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioContextRef.current;
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = "sine";
      
      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + duration);
    } catch (e) {
      // Audio API not supported - silently continue without sound
      if (process.env.NODE_ENV === 'development') {
        console.warn('Audio playback not supported:', e.message);
      }
    }
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    setTimeLeft(TIMER_MODES[newMode].duration);
    setIsRunning(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const toggleTimer = () => {
    if (isRunning) {
      setIsRunning(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    } else {
      setIsRunning(true);
      playSound(600, 0.15);
    }
  };

  const resetTimer = () => {
    setIsRunning(false);
    setTimeLeft(TIMER_MODES[mode].duration);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            setIsRunning(false);
            playSound(1000, 0.5);
            
            // Handle session completion
            if (mode === "focus") {
              setSessions((prev) => prev + 1);
              setTotalFocusTime((prev) => prev + TIMER_MODES.focus.duration);
              
              // Auto-switch to break
              const newSessions = sessions + 1;
              if (newSessions % 4 === 0) {
                setMode("longBreak");
                return TIMER_MODES.longBreak.duration;
              } else {
                setMode("shortBreak");
                return TIMER_MODES.shortBreak.duration;
              }
            } else {
              // Break finished, switch back to focus
              setMode("focus");
              return TIMER_MODES.focus.duration;
            }
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRunning, timeLeft, mode, sessions, playSound]);

  // Track focus time for current session
  useEffect(() => {
    if (isRunning && mode === "focus") {
      const trackingInterval = setInterval(() => {
        setTotalFocusTime((prev) => prev + 1);
      }, 1000);
      return () => clearInterval(trackingInterval);
    }
  }, [isRunning, mode]);

  const progress = ((TIMER_MODES[mode].duration - timeLeft) / TIMER_MODES[mode].duration) * 100;

  return (
    <div className="min-h-screen bg-[#0b2821] pb-24" data-testid="focus-timer-page">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-[#0b2821]/95 backdrop-blur-sm border-b border-[#a69373]/20">
        <div className="px-4 py-4 flex items-center gap-3">
          <button 
            onClick={() => navigate("/compass")}
            className="p-2 -ml-2"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-rose-500/20 to-red-500/20 flex items-center justify-center">
              <Clock className="w-4 h-4 text-[#a69373]" />
            </div>
            <h1 className="text-lg font-heading text-[#a69373]">Focus Timer</h1>
          </div>
        </div>
      </header>

      <main className="px-4 py-6">
        {/* Mode Selector */}
        <div className="flex gap-2 mb-8">
          {Object.entries(TIMER_MODES).map(([key, value]) => (
            <button
              key={key}
              onClick={() => handleModeChange(key)}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                mode === key
                  ? "bg-[#a69373] text-[#0b2821]"
                  : "bg-white/10 text-white hover:bg-white/20"
              }`}
              data-testid={`mode-${key}`}
            >
              {value.label}
            </button>
          ))}
        </div>

        {/* Timer Display */}
        <div className="text-center mb-8">
          <div className={`relative w-64 h-64 mx-auto rounded-full bg-gradient-to-br ${currentMode.color} flex items-center justify-center`}>
            {/* Progress ring */}
            <svg className="absolute inset-0 w-full h-full -rotate-90">
              <circle
                cx="128"
                cy="128"
                r="120"
                fill="none"
                stroke="rgba(255,255,255,0.1)"
                strokeWidth="8"
              />
              <circle
                cx="128"
                cy="128"
                r="120"
                fill="none"
                stroke="rgba(166,147,115,0.8)"
                strokeWidth="8"
                strokeDasharray={2 * Math.PI * 120}
                strokeDashoffset={2 * Math.PI * 120 * (1 - progress / 100)}
                strokeLinecap="round"
                className="transition-all duration-1000"
              />
            </svg>
            
            <div className="z-10">
              <p className={`text-6xl font-mono font-bold ${currentMode.textColor}`}>
                {formatTime(timeLeft)}
              </p>
              <p className="text-white/50 text-sm mt-2">{currentMode.label}</p>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="flex justify-center gap-4 mb-8">
          <Button
            variant="outline"
            size="lg"
            onClick={resetTimer}
            className="w-14 h-14 rounded-full border-white/20 text-white hover:bg-white/10"
            data-testid="reset-button"
          >
            <RotateCcw className="w-6 h-6" />
          </Button>
          
          <Button
            size="lg"
            onClick={toggleTimer}
            className={`w-20 h-20 rounded-full ${
              isRunning 
                ? "bg-white/20 hover:bg-white/30" 
                : "bg-[#a69373] hover:bg-[#8a7a5f]"
            } text-white`}
            data-testid="play-pause-button"
          >
            {isRunning ? (
              <Pause className="w-8 h-8" />
            ) : (
              <Play className="w-8 h-8 ml-1" />
            )}
          </Button>
          
          <Button
            variant="outline"
            size="lg"
            onClick={() => handleModeChange(mode === "focus" ? "shortBreak" : "focus")}
            className="w-14 h-14 rounded-full border-white/20 text-white hover:bg-white/10"
            data-testid="skip-button"
          >
            {mode === "focus" ? <Coffee className="w-6 h-6" /> : <Target className="w-6 h-6" />}
          </Button>
        </div>

        {/* Stats */}
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Target className="w-4 h-4 text-[#a69373]" />
                  <span className="text-white/50 text-sm">Sessions</span>
                </div>
                <p className="text-2xl font-bold text-white">{sessions}</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-[#a69373]" />
                  <span className="text-white/50 text-sm">Focus Time</span>
                </div>
                <p className="text-2xl font-bold text-white">
                  {Math.floor(totalFocusTime / 60)}m
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Info */}
        <div className="mt-6 bg-white/5 rounded-xl p-4 border border-white/10">
          <h3 className="text-white font-medium mb-2">Pomodoro Technique</h3>
          <ul className="text-white/50 text-sm space-y-1">
            <li className="flex items-center gap-2">
              <Check className="w-3 h-3 text-[#a69373]" />
              25 minutes of focused work
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-3 h-3 text-[#a69373]" />
              5 minute short break
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-3 h-3 text-[#a69373]" />
              15 minute long break after 4 sessions
            </li>
          </ul>
        </div>
      </main>

      <BottomNavigation />
    </div>
  );
}
